import { useCallback, useEffect, useState } from "react";

import { FileText, Plus } from "lucide-react";
import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { usePagination } from "../../hooks/usePagination";

import InvoiceDialog from "./component/InvoiceDialog";
import InvoicesTab from "./component/InvoicesTab";

const Invoices = () => {
  const { user } = useAuth();
  const api = useApi();
  const { globalProjectId } = useData();
  const [allVendors, setAllVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [vendorFilter, setVendorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);

  const invoicePagination = usePagination(10);
  const {
    items: invoices,
    currentPage: invoicePage,
    pageSize: invoicePageSize,
    totalItems: invoiceTotalItems,
    setPaginationData: setInvoicePaginationData,
    setCurrentPage: setInvoicePage
  } = invoicePagination;

  const fetchAllVendors = useCallback(async () => {
    try {
      const response = await api.get(`/vendors?page=0&size=1000`);
      if (response.status === 200) {
        const vendorList = response.data.vendors || response.data.items || response.data.content || [];
        setAllVendors(vendorList);
      }
    } catch (error) {
      console.error("Error fetching all vendors:", error);
    }
  }, [api]);

  const fetchInvoices = useCallback(
    async (page) => {
      try {
        setLoading(true);
        const response = await api.get(`/invoices`, {
          params: {
            page,
            size: invoicePageSize,
            search: invoiceSearch.trim() || undefined,
            vendorId: vendorFilter !== "all" ? vendorFilter : undefined,
            projectId: globalProjectId === "ALL" ? undefined : globalProjectId,
            status: statusFilter !== "all" ? statusFilter : undefined,
            sortBy: sortBy || undefined,
            direction: direction || undefined,
            showDeleted: showDeleted
          }
        });
        setInvoicePaginationData(response.data);
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      invoicePageSize,
      invoiceSearch,
      vendorFilter,
      globalProjectId,
      statusFilter,
      sortBy,
      direction,
      showDeleted,
      setInvoicePaginationData
    ]
  );

  useEffect(() => {
    fetchAllVendors();
  }, [fetchAllVendors]);

  useEffect(() => {
    setInvoicePage(0);
  }, [globalProjectId, setInvoicePage]);

  useEffect(() => {
    fetchInvoices(invoicePage);
  }, [invoicePage, invoicePageSize, fetchInvoices]);

  const handleAddInvoice = () => {
    setEditingInvoice(null);
    setIsInvoiceDialogOpen(true);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setIsInvoiceDialogOpen(true);
  };

  const handleFilterChange = (type, value) => {
    if (type === "vendor") setVendorFilter(value);
    if (type === "status") setStatusFilter(value);
    if (type === "sortBy") setSortBy(value);
    if (type === "direction") setDirection(value);
    setInvoicePage(0);
  };

  if (!user?.permissions?.invoice?.read && user?.role !== "super_admin") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-yale-blue-50 dark:bg-yale-blue-900/50 rounded-xl border border-dashed border-slate-200 dark:border-yale-blue-800">
        <div className="bg-slate-200 dark:bg-yale-blue-800 p-4 rounded-full mb-4">
          <FileText className="w-8 h-8 text-slate-400 dark:text-yale-blue-500" />
        </div>
        <h3 className="text-xl font-semibold text-yale-blue-900 dark:text-yale-blue-50 mb-2">Access Restricted</h3>
        <p className="text-yale-blue-500 dark:text-slate-400 max-w-xs mx-auto">
          You don't have permission to view invoices. Please contact your system administrator for access.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 flex flex-col pb-8">
        <PageHeader
          title="Invoices"
          subtitle="Track and manage vendor invoices"
          pageIcon={FileText}
          actionLabel={user?.permissions?.invoice?.create ? "Add Invoice" : null}
          onActionClick={handleAddInvoice}
          icon={Plus}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput
                placeholder="Search invoices..."
                className="w-full md:w-64"
                onSearch={(val) => {
                  setInvoiceSearch(val);
                  setInvoicePage(0);
                }}
                value={invoiceSearch}
              />
            </div>
          }
        />

        <div className="flex-1 flex flex-col">
          <InvoicesTab
            invoices={invoices}
            vendors={allVendors}
            fetchInvoices={() => fetchInvoices(invoicePage)}
            onEditInvoice={handleEditInvoice}
            filters={{
              vendorId: vendorFilter,
              status: statusFilter,
              sortBy,
              direction
            }}
            onFilterChange={handleFilterChange}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
            setInvoicePage={setInvoicePage}
          />
        </div>

        <div className="mt-auto pt-6">
          <Pagination pagination={invoicePagination} />
        </div>
      </div>

      <InvoiceDialog
        isOpen={isInvoiceDialogOpen}
        onOpenChange={setIsInvoiceDialogOpen}
        onSuccess={() => fetchInvoices(invoicePage)}
        editingInvoice={editingInvoice}
        vendors={allVendors}
        selectedProject={globalProjectId}
      />
    </div>
  );
};

export default Invoices;
